import { useField } from 'formik';
import { FC, useMemo } from 'react';

import { Checkbox } from '@components/uiKit/Checkbox';

import { FILTER_POPUP_COLLAPSED_COUNT } from '@scripts/constants';
import { themeColor } from '@scripts/themes/cssVars';

import { showAllButtonStyles } from '../../../../../../styles';
import { getFieldName } from '../../../../../../utils';
import { sortFilterItems } from '../../../../../../utils/sortFilterItems';
import { checkboxesGridStyles, getCheckboxesContainerStyles, getCheckboxStyles } from './styles';
import { FilterCheckboxesProps } from './types';
import { toggleArrayValue } from './utils';

const FilterCheckboxes: FC<FilterCheckboxesProps> = ({
    filterField,
    fieldPrefix,
    formKey,
    filterCode,
    displayedItems,
    filterItems,
    isExpanded,
    onToggle,
    onFilterChange,
}) => {
    const fieldName = getFieldName(String(filterField.code), fieldPrefix);
    const [field, _, helpers] = useField<string[]>(fieldName);

    const sortedFilterItems = useMemo(() => {
        const selectedValues = field.value || [];

        return sortFilterItems(filterItems, selectedValues);
    }, [filterItems, field.value]);

    const sortedDisplayedItems = useMemo(() => {
        if (isExpanded) {
            return sortedFilterItems;
        }

        return sortedFilterItems.slice(0, FILTER_POPUP_COLLAPSED_COUNT);
    }, [sortedFilterItems, isExpanded]);

    return (
        <>
            <div css={getCheckboxesContainerStyles(isExpanded)}>
                <div css={checkboxesGridStyles}>
                    {sortedDisplayedItems.map(item => {
                        const count = 'products_count' in item ? item.products_count : undefined;
                        const fieldValue = field?.value || [];
                        const isSelected = fieldValue.includes(String(item.code));
                        const isZeroCount = count === 0;
                        const isDisabled = isZeroCount && !isSelected;

                        return (
                            <Checkbox
                                key={item.code}
                                value={item.code}
                                id={formKey ? `${formKey}-${filterCode}-${item.code}` : undefined}
                                disabled={isDisabled}
                                field={{
                                    ...field,
                                    value: fieldValue,
                                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const itemValue = String(item.code || e.target.value);
                                        const valueArray = field?.value || [];
                                        const newValue = toggleArrayValue(valueArray, itemValue);
                                        helpers?.setValue(newValue);
                                        onFilterChange?.(fieldName, newValue);
                                    },
                                }}
                                customCss={{ wrapper: getCheckboxStyles(isDisabled) }}
                            >
                                <span>
                                    {String(item.name || ('value' in item ? item.value : '') || '')}
                                    {count !== undefined && count !== null && (
                                        <span css={{ color: themeColor.textLight, marginLeft: '8px' }}>
                                            {String(count)}
                                        </span>
                                    )}
                                </span>
                            </Checkbox>
                        );
                    })}
                </div>
            </div>
            {sortedFilterItems.length > FILTER_POPUP_COLLAPSED_COUNT && (
                <button type="button" onClick={() => onToggle(filterCode)} css={showAllButtonStyles}>
                    {isExpanded ? 'Скрыть' : 'Показать все'}
                </button>
            )}
        </>
    );
};

export default FilterCheckboxes;
